import { useMemo, useState } from 'react';
import { missingKasChains } from '../../components/kasPromoCode';
import {
  Alert,
  App,
  Badge,
  Button,
  Card,
  Checkbox,
  Dropdown,
  Input,
  Modal,
  Popover,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DataTable } from '../../components/DataTable';
import { StatusTag } from '../../components/StatusTag';
import {
  DotsThreeVertical,
  FlagCheckered,
  FunnelSimple,
  Pause,
  PencilSimple,
  Play,
  Plus,
  TestTube,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { ApiError, NetworkError } from '../../api/client';
import { catalogApi, catalogKeys } from '../catalog/api';
import { tokens } from '../../theme';
import {
  KIND_LABELS,
  KIND_OPTIONS,
  STATE_LABELS,
  audienceLabel,
  budgetLabel,
  campaignApi,
  campaignKeys,
  dryRunSummary,
  scheduleLabel,
  statusLabel,
  type Campaign,
  type CampaignKind,
  type CampaignRun,
  type CampaignState,
  type UpsertCampaignPayload,
} from './api';
import { CampaignFormModal } from './CampaignFormModal';

const KIND_TAG_COLOR: Record<CampaignKind, string> = {
  Welcome: 'blue',
  Birthday: 'blue',
  FlashPoints: 'orange',
  WinBack: 'orange',
};

export function CampaignsPage() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const [kindFilter, setKindFilter] = useState<CampaignKind | undefined>();
  const [stateFilter, setStateFilter] = useState<CampaignState | undefined>();
  const [includeEnded, setIncludeEnded] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [pauseTarget, setPauseTarget] = useState<Campaign | null>(null);
  const [pauseReason, setPauseReason] = useState('');
  const [dryRunResult, setDryRunResult] = useState<CampaignRun | null>(null);
  const [lastDryRunId, setLastDryRunId] = useState<string | null>(null);

  const filters = useMemo(
    () => ({ kind: kindFilter, state: stateFilter, includeEnded }),
    [kindFilter, stateFilter, includeEnded],
  );

  const listQuery = useQuery({
    queryKey: campaignKeys.list(filters),
    queryFn: () => campaignApi.list(filters),
  });
  const chainsQuery = useQuery({ queryKey: catalogKeys.chains, queryFn: catalogApi.listChains });

  const reportError = (error: unknown, fallback: string) => {
    message.error(
      error instanceof ApiError || error instanceof NetworkError ? error.message : fallback,
    );
  };

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: campaignKeys.all });
  };

  const saveMutation = useMutation({
    mutationFn: (payload: { id?: string; body: UpsertCampaignPayload }) =>
      payload.id ? campaignApi.update(payload.id, payload.body) : campaignApi.create(payload.body),
    onSuccess: async () => {
      await invalidate();
      message.success('Đã lưu chương trình.');
      setEditing(null);
      setIsCreating(false);

      // 🔴 Sửa chương trình thì PHẢI đóng lại cổng "đã chạy thử".
      //
      // Cổng ở `confirmRun` tồn tại để bắt admin **đọc số tiền rồi mới chạy**. Nhưng con số đó
      // thuộc về cấu hình lúc chạy thử. Không xóa dấu ở đây thì lọt đúng chuỗi này:
      //   1. chạy thử → "438 khách · 87,6 triệu", cổng mở
      //   2. sửa: đổi sang voucher mệnh giá lớn hơn, hoặc nâng trần
      //   3. bấm "Chạy thật" → cổng VẪN mở, chạy với con số admin chưa bao giờ đọc
      // Trần `max_total_value` ở backend vẫn chặn vượt hạn, nhưng admin duyệt một con số rồi thực
      // thi một con số khác — đúng loại rủi ro cái cổng này sinh ra để chặn.
      setLastDryRunId(null);
    },
    onError: (error) => reportError(error, 'Không lưu được chương trình.'),
  });

  const activateMutation = useMutation({
    mutationFn: campaignApi.activate,
    onSuccess: async () => {
      await invalidate();
      message.success('Đã bật chương trình.');
    },
    onError: (error) => reportError(error, 'Không bật được chương trình.'),
  });

  const pauseMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => campaignApi.pause(id, reason),
    onSuccess: async () => {
      await invalidate();
      message.success('Đã tạm dừng chương trình.');
      setPauseTarget(null);
      setPauseReason('');
    },
    onError: (error) => reportError(error, 'Không tạm dừng được chương trình.'),
  });

  const endMutation = useMutation({
    mutationFn: campaignApi.end,
    onSuccess: async () => {
      await invalidate();
      message.success('Đã kết thúc chương trình.');
    },
    onError: (error) => reportError(error, 'Không kết thúc được chương trình.'),
  });

  const dryRunMutation = useMutation({
    mutationFn: campaignApi.dryRun,
    onSuccess: (run) => {
      setDryRunResult(run);
      setLastDryRunId(run.campaignId);
    },
    onError: (error) => reportError(error, 'Không chạy thử được.'),
  });

  const runMutation = useMutation({
    mutationFn: campaignApi.run,
    onSuccess: async (run) => {
      await invalidate();
      setLastDryRunId(null);
      modal.success({
        title: 'Đã chạy chương trình',
        content: `${dryRunSummary(run)}${run.stoppedBecause ? ` · ${run.stoppedBecause}` : ''}`,
      });
    },
    onError: (error) => reportError(error, 'Không chạy được chương trình.'),
  });

  const confirmEnd = (row: Campaign) => {
    modal.confirm({
      title: `Kết thúc “${row.name}”?`,
      content: 'Không bật lại được. Khác tạm dừng: tạm dừng còn nâng trần rồi bật tiếp.',
      okText: 'Kết thúc',
      okButtonProps: { danger: true },
      cancelText: 'Hủy',
      onOk: () => endMutation.mutateAsync(row.id),
    });
  };

  const confirmRun = (row: Campaign) => {
    if (lastDryRunId !== row.id) {
      message.warning('Chạy thử trước, đọc số tiền, rồi mới chạy thật.');
      return;
    }
    modal.confirm({
      title: `Chạy thật “${row.name}”?`,
      content: 'Đi qua đúng mọi phép chặn của job. Bấm lần hai không phát thêm nếu đã gửi trong chu kỳ này.',
      okText: 'Chạy thật',
      cancelText: 'Hủy',
      onOk: () => runMutation.mutateAsync(row.id),
    });
  };

  const lowBudgetRows = (listQuery.data ?? []).filter((c) => c.isLowBudget);
  const busy =
    activateMutation.isPending ||
    pauseMutation.isPending ||
    endMutation.isPending ||
    dryRunMutation.isPending ||
    runMutation.isPending;

  const columns: ColumnsType<Campaign> = [
    {
      title: 'Tên chương trình',
      key: 'name',
      render: (_, row) => (
        <>
          <div style={{ fontWeight: 600 }}>{row.name}</div>
          {row.description && (
            <div style={{ fontSize: 12, color: tokens.color.ink3 }}>{row.description}</div>
          )}
        </>
      ),
    },
    {
      title: 'Loại',
      dataIndex: 'kind',
      width: 140,
      render: (kind: CampaignKind) => <Tag color={KIND_TAG_COLOR[kind]}>{KIND_LABELS[kind]}</Tag>,
    },
    {
      title: 'Đối tượng',
      key: 'audience',
      width: 200,
      render: (_, row) => <span style={{ color: tokens.color.ink2 }}>{audienceLabel(row)}</span>,
    },
    {
      title: 'Lịch chạy',
      key: 'schedule',
      width: 180,
      render: (_, row) => <span className="mono">{scheduleLabel(row)}</span>,
    },
    {
      title: 'Ngân sách',
      key: 'budget',
      width: 180,
      align: 'right',
      render: (_, row) => {
        const budget = budgetLabel(row);
        return (
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              color: budget.unlimited || row.isLowBudget ? tokens.color.warn : undefined,
              fontWeight: row.isLowBudget ? 600 : undefined,
            }}
          >
            {budget.text}
          </span>
        );
      },
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 160,
      render: (_, row) => {
        const status = statusLabel(row);
        return (
          <Space size={4} wrap>
            <StatusTag color={status.tone}>{status.label}</StatusTag>
            {row.isLowBudget && <StatusTag color="warning">Sắp cạn ngân sách</StatusTag>}
          </Space>
        );
      },
    },
    {
      // Mã là tuỳ chọn nên thiếu mã KHÔNG phải lỗi — tông cảnh báo, không phải tông lỗi. Cột này
      // bắt chỗ "khai xong rồi quên" trước khi ra quầy, chứ không chặn ai.
      title: 'Mã KAS',
      key: 'kas',
      width: 150,
      render: (_, row) => {
        const missing = missingKasChains(row.kasPromoCodes, row.chainId, chainsQuery.data ?? []);
        if (missing.length === 0) return <StatusTag color="success">Đã khai</StatusTag>;
        return (
          <Tooltip title={`Chưa khai cho: ${missing.join(', ')}`}>
            <StatusTag color="warning">Thiếu {missing.length} chuỗi</StatusTag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => {
        const canActivate = row.state === 'Draft' || row.state === 'Paused';
        const canPause = row.state === 'Active';
        const canEnd = row.state !== 'Ended';
        const isEnded = row.state === 'Ended';
        const activateBlocked = row.missingFields.length > 0;

        // Một menu thay cho 6 nút xếp tràn dòng. Mục nào không dùng được ở trạng thái hiện tại thì
        // ẩn hẳn (Bật/Tạm dừng), còn mục còn dùng được nhưng đang bị chặn thì để mờ (disabled)
        // kèm lý do — người dùng vẫn thấy nó tồn tại và biết vì sao chưa bấm được.
        const items: MenuProps['items'] = [
          {
            key: 'edit',
            icon: <PencilSimple size={15} />,
            label: 'Sửa',
            onClick: () => setEditing(row),
          },
          ...(canActivate
            ? [
                {
                  key: 'activate',
                  icon: <Play size={15} />,
                  label: activateBlocked
                    ? `Bật — còn thiếu: ${row.missingFields.join(', ')}`
                    : 'Bật',
                  disabled: activateBlocked || busy,
                  onClick: () => activateMutation.mutate(row.id),
                },
              ]
            : []),
          ...(canPause
            ? [
                {
                  key: 'pause',
                  icon: <Pause size={15} />,
                  label: 'Tạm dừng',
                  disabled: busy,
                  onClick: () => setPauseTarget(row),
                },
              ]
            : []),
          { type: 'divider' as const },
          {
            key: 'dryRun',
            icon: <TestTube size={15} />,
            label: 'Chạy thử',
            disabled: isEnded || busy,
            onClick: () => dryRunMutation.mutate(row.id),
          },
          {
            key: 'run',
            icon: <Play size={15} weight="fill" />,
            // Không disable theo dry-run: để `confirmRun` nhắc "chạy thử trước" khi cần, thay vì
            // một mục xám không giải thích được vì sao.
            label: lastDryRunId === row.id ? 'Chạy thật' : 'Chạy thật (cần chạy thử trước)',
            disabled: isEnded || busy,
            onClick: () => confirmRun(row),
          },
          ...(canEnd
            ? [
                { type: 'divider' as const },
                {
                  key: 'end',
                  icon: <FlagCheckered size={15} />,
                  label: 'Kết thúc',
                  danger: true,
                  disabled: busy,
                  onClick: () => confirmEnd(row),
                },
              ]
            : []),
        ];

        return (
          <Dropdown trigger={['click']} menu={{ items }} placement="bottomRight">
            <Button
              type="text"
              size="small"
              icon={<DotsThreeVertical size={18} weight="bold" />}
              aria-label={`Thao tác với ${row.name}`}
            />
          </Dropdown>
        );
      },
    },
  ];

  // Gom bộ lọc vào MỘT nút "Lọc" (như trang Đánh giá bữa ăn): loại · trạng thái · hiện đã kết thúc.
  // Badge đếm số bộ lọc đang thu hẹp.
  const activeFilterCount =
    (kindFilter ? 1 : 0) + (stateFilter ? 1 : 0) + (includeEnded ? 1 : 0);

  const resetFilters = () => {
    setKindFilter(undefined);
    setStateFilter(undefined);
    setIncludeEnded(false);
  };

  const fieldLabel = (text: string) => (
    <span style={{ display: 'block', fontSize: 12, color: tokens.color.ink2, marginBottom: 4 }}>
      {text}
    </span>
  );

  const filterControl = (
    <Popover
      trigger="click"
      placement="bottomRight"
      content={
        <Space direction="vertical" size={12} style={{ width: 240 }}>
          <div>
            {fieldLabel('Loại')}
            <Select
              allowClear
              value={kindFilter}
              onChange={setKindFilter}
              style={{ width: '100%' }}
              placeholder="Mọi loại"
              options={KIND_OPTIONS}
            />
          </div>
          <div>
            {fieldLabel('Trạng thái')}
            <Select
              allowClear
              value={stateFilter}
              onChange={setStateFilter}
              style={{ width: '100%' }}
              placeholder="Mọi trạng thái"
              options={(Object.entries(STATE_LABELS) as [CampaignState, string][]).map(
                ([value, label]) => ({ value, label }),
              )}
            />
          </div>
          <Checkbox checked={includeEnded} onChange={(e) => setIncludeEnded(e.target.checked)}>
            Hiện đã kết thúc
          </Checkbox>
          <Button
            type="link"
            size="small"
            onClick={resetFilters}
            disabled={activeFilterCount === 0}
            style={{ padding: 0, alignSelf: 'flex-start' }}
          >
            Đặt lại bộ lọc
          </Button>
        </Space>
      }
    >
      <Badge count={activeFilterCount} size="small" offset={[-4, 2]}>
        <Button icon={<FunnelSimple size={15} aria-hidden />} size="small">
          Lọc
        </Button>
      </Badge>
    </Popover>
  );

  return (
    <>
      <PageHeader
        title="Khuyến mãi tự động"
        subtitle="Cấu hình"
        actions={
          <Button type="primary" icon={<Plus size={16} />} onClick={() => setIsCreating(true)}>
            Tạo khuyến mãi
          </Button>
        }
      />
      <PageBody>
        <Typography.Paragraph
          type="secondary"
          style={{ marginTop: 0, marginBottom: tokens.space.sectionGap }}
        >
          Mỗi loại khuyến mãi có sẵn mẫu cấu hình. Trước khi bật thật, chạy chế độ thử để biết bao
          nhiêu khách nằm trong đối tượng và tốn bao nhiêu.
        </Typography.Paragraph>

        {lowBudgetRows.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              lowBudgetRows.length === 1
                ? `${lowBudgetRows[0].name} sắp cạn ngân sách. Hệ thống sẽ tự tạm dừng khi cạn và gửi cảnh báo.`
                : `${lowBudgetRows.length} chương trình sắp cạn ngân sách. Hệ thống sẽ tự tạm dừng khi cạn và gửi cảnh báo.`
            }
          />
        )}

        {listQuery.isPending && <LoadingState />}
        {listQuery.isError && (
          <ErrorState error={listQuery.error} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.isSuccess && (
          <Card styles={{ body: { paddingTop: 8 } }}>
            <DataTable
              title="Chương trình khuyến mãi"
              subTitle={`${listQuery.data.length} chương trình`}
              rowKey="id"
              size="small"
              columns={columns}
              dataSource={listQuery.data}
              onReload={() => void listQuery.refetch()}
              searchPlaceholder="Tìm khuyến mãi"
              pagination={{ pageSize: 20, showSizeChanger: false }}
              scroll={{ x: 1400 }}
              locale={{
                emptyText:
                  kindFilter || stateFilter || includeEnded
                    ? 'Không có chương trình nào khớp bộ lọc.'
                    : 'Chưa có chương trình nào. Bấm “Tạo khuyến mãi” để thêm.',
              }}
              toolbarExtra={filterControl}
            />
          </Card>
        )}
      </PageBody>

      <CampaignFormModal
        campaign={editing}
        isCreating={isCreating}
        chains={chainsQuery.data ?? []}
        submitting={saveMutation.isPending}
        onClose={() => {
          setEditing(null);
          setIsCreating(false);
        }}
        onSubmit={(body) => {
          if (isCreating) saveMutation.mutate({ body });
          else if (editing) saveMutation.mutate({ id: editing.id, body });
        }}
      />

      <Modal
        open={pauseTarget !== null}
        title={`Tạm dừng “${pauseTarget?.name ?? ''}”?`}
        okText="Tạm dừng"
        cancelText="Hủy"
        confirmLoading={pauseMutation.isPending}
        onCancel={() => {
          setPauseTarget(null);
          setPauseReason('');
        }}
        onOk={() => {
          if (pauseTarget) {
            pauseMutation.mutate({ id: pauseTarget.id, reason: pauseReason.trim() || undefined });
          }
        }}
      >
        <Input.TextArea
          rows={3}
          value={pauseReason}
          onChange={(e) => setPauseReason(e.target.value)}
          placeholder="Lý do (không bắt buộc). Để trống thì server ghi “Tạm dừng bằng tay từ Admin Web”."
        />
      </Modal>

      <Modal
        open={dryRunResult !== null}
        title="Chạy thử — không phát gì"
        okText="Đóng"
        cancelButtonProps={{ style: { display: 'none' } }}
        onOk={() => setDryRunResult(null)}
        onCancel={() => setDryRunResult(null)}
      >
        {dryRunResult && (
          <>
            <Typography.Title level={3} style={{ marginTop: 0 }}>
              {dryRunSummary(dryRunResult)}
            </Typography.Title>
            <Typography.Paragraph type="secondary">
              Số tiền là điểm chính: đọc “438 khách” thì không ai giật mình; đọc số tiền thì người
              bật tự dừng tay. Chế độ này không cộng vào trần và không tạo voucher.
            </Typography.Paragraph>
            {dryRunResult.stoppedBecause && (
              <Alert type="warning" showIcon message={dryRunResult.stoppedBecause} />
            )}
          </>
        )}
      </Modal>
    </>
  );
}
